import { useState, useRef } from 'react';
import {
    Chip,
    IconButton,
    Box,
    Typography,
    Stack,
    Popper,
    Paper,
    ClickAwayListener,
    Divider,
    Table,
    TableRow,
    TableCell,
    TableBody,
    useTheme,
    Tooltip,
    Collapse,
    Button
} from '@mui/material';

import { CalendarMonth, ExpandLess, ExpandMore, MoreHoriz } from '@mui/icons-material';

const formatReadableDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
    }).format(date);
};

const formatDateItem = (item) => {
    if (item.date) return formatReadableDate(item.date);
    if (item.start && item.end) {
        return `${formatReadableDate(item.start)} - ${formatReadableDate(item.end)}`;
    }
    return '';
};


const DateChips = ({ data, detailview, postview, worklistview }) => {
    const theme = useTheme();
    const [open, setOpen] = useState(false);
    const [hovered, setHovered] = useState(false);
    const iconRef = useRef(null);

    const handleClickToggle = () => setOpen((prev) => !prev);

    const handleMouseEnter = () => setHovered(true);
    const handleMouseLeave = () => setHovered(false);

    const handleClickAway = () => {
        // Only close if it's open from click and not being hovered
        if (!hovered) setOpen(false);
    };

    const shouldShowPopper = open || hovered;

    const visibleItems = data.slice(0, 2);
    const hiddenItems = data.slice(2);

    const [showAll, setShowAll] = useState(false);
    const handleClickShowAllDates = () => {
        setShowAll((prev) => !prev);
    };

    return (
        <>
            {worklistview && (
                <Stack direction="row" spacing={0.5} gap={0.5} alignItems="center" flexWrap="wrap">
                    {visibleItems.length > 0 && (
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                            <CalendarMonth sx={{ color: theme.palette.text.secondary }} />
                            <Typography variant="body2">
                                {visibleItems.length > 1
                                    ? `Dates :`
                                    : `Date :`
                                }
                            </Typography>
                        </Box>
                    )}
                    {visibleItems.map((item, index) => (
                        <Paper
                            variant='outlined'
                            key={index}
                            sx={{ display: 'flex', boxShadow: 'none', alignItems: 'center', borderRadius: 5, backgroundColor: 'background.paper' }}
                        >
                            <Tooltip title={formatDateItem(item)} placement="top" arrow>
                                 <Chip clickable={false}
                            onClick={() => {}}
                                    label={formatDateItem(item)}
                                    color='inherit'
                                    size="small"
                                    sx={{
                                        backgroundColor: 'background.default',
                                        maxWidth: '140px', // Explicit max width
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap', // Ensure single-line text
                                    }}
                                />
                            </Tooltip>

                        </Paper>
                    ))}

                    {hiddenItems.length > 0 && (
                        <>
                            <Paper variant='outlined' sx={{ borderRadius: '16px', }}>
                                <IconButton
                                    ref={iconRef}
                                    onClick={handleClickToggle}
                                    onMouseEnter={handleMouseEnter}
                                    onMouseLeave={handleMouseLeave}
                                    size="small"
                                >
                                    <MoreHoriz sx={{ fontSize: '14px' }} />
                                </IconButton>
                            </Paper>

                            <Popper
                                open={shouldShowPopper}
                                anchorEl={iconRef.current}
                                placement="top-end"
                                disablePortal
                                modifiers={[
                                    {
                                        name: 'offset',
                                        options: {
                                            offset: [0, 8],
                                        },
                                    },
                                ]}
                                sx={{
                                    zIndex: 1,
                                    transition: 'opacity 0.3s ease',
                                    opacity: shouldShowPopper ? 1 : 0,
                                }}
                            >
                                <ClickAwayListener onClickAway={handleClickAway}>
                                    <Paper
                                        elevation={8}
                                        onMouseEnter={handleMouseEnter}
                                        onMouseLeave={handleMouseLeave}
                                        sx={{
                                            maxHeight: '260px',
                                            overflowY: 'auto',
                                            scrollbarWidth: 'thin',
                                        }}
                                    >
                                        <Paper
                                            elevation={8}
                                            sx={{
                                                padding: '8px 16px',
                                                position: 'sticky',
                                                top: 0,
                                                zIndex: 1,
                                                boxShadow: 'none',
                                            }}
                                        >
                                            <Typography variant="body2">
                                                All Dates
                                            </Typography>
                                        </Paper>
                                        <Divider />

                                        <Table size="small">
                                            <TableBody>
                                                {data.map((item, index) => (
                                                    <TableRow
                                                        key={index}
                                                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                                    >
                                                        <TableCell>
                                                            {index + 1}. {formatDateItem(item)}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </Paper>
                                </ClickAwayListener>
                            </Popper>
                        </>
                    )}
                </Stack>
            )}

            {postview && (
                <Stack direction="row" spacing={0.5} gap={0.5} alignItems="center" flexWrap="wrap" justifyContent={'flex-start'}>
                    {visibleItems.length > 0 && (
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                            <CalendarMonth sx={{ color: theme.palette.text.secondary, fontSize: '1.2rem' }} />
                            <Typography variant="body2">
                                {visibleItems.length > 1
                                    ? <strong>Dates : </strong>
                                    : <strong>Date : </strong>
                                }
                            </Typography>
                        </Box>
                    )}
                    {visibleItems.map((item, index) => (
                        <Paper
                            variant='outlined'
                            key={index}
                            sx={{ display: 'flex', boxShadow: 'none', alignItems: 'center', borderRadius: 5, backgroundColor: 'background.paper' }}
                        >
                            <Tooltip title={formatDateItem(item)} placement="top" arrow>
                                 <Chip clickable={false}
                            onClick={() => {}}
                                    label={formatDateItem(item)}
                                    color='inherit'
                                    size="small"
                                    sx={{
                                        backgroundColor: 'background.default',
                                        maxWidth: '140px', // Explicit max width
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap', // Ensure single-line text
                                    }}
                                />
                            </Tooltip>
                        </Paper>
                    ))}

                    {hiddenItems.length > 0 && (
                        <>
                        <Paper variant='outlined' sx={{ borderRadius: '16px', }}>
                            <IconButton
                                ref={iconRef}
                                onClick={handleClickToggle}
                                onMouseEnter={handleMouseEnter}
                                onMouseLeave={handleMouseLeave}
                                size="small"
                                color='inherit'
                            >
                                <MoreHoriz  sx={{ fontSize: '14px' }}  />
                            </IconButton>
                            </Paper>

                            <Popper
                                open={shouldShowPopper}
                                anchorEl={iconRef.current}
                                placement="top-end"
                                disablePortal
                                modifiers={[
                                    {
                                        name: 'offset',
                                        options: {
                                            offset: [0, 8],
                                        },
                                    },
                                ]}
                                sx={{
                                    zIndex: 1,
                                    transition: 'opacity 0.3s ease',
                                    opacity: shouldShowPopper ? 1 : 0,
                                }}
                            >
                                <ClickAwayListener onClickAway={handleClickAway}>
                                    <Paper
                                        elevation={8}
                                        onMouseEnter={handleMouseEnter}
                                        onMouseLeave={handleMouseLeave}
                                        sx={{
                                            maxHeight: '260px',
                                            overflowY: 'auto',
                                            scrollbarWidth: 'thin',
                                        }}
                                    >
                                        <Paper
                                            elevation={8}
                                            sx={{
                                                padding: '8px 16px',
                                                position: 'sticky',
                                                top: 0,
                                                zIndex: 1,
                                                boxShadow: 'none',
                                            }}
                                        >
                                            <Typography variant="body2">
                                                All Dates
                                            </Typography>
                                        </Paper>
                                        <Divider />

                                        <Table size="small">
                                            <TableBody>
                                                {data.map((item, index) => (
                                                    <TableRow
                                                        key={index}
                                                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                                    >
                                                        <TableCell>
                                                            {index + 1}. {formatDateItem(item)}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </Paper>
                                </ClickAwayListener>
                            </Popper>
                        </>
                    )}
                </Stack>
            )}

            {detailview && (
                <Stack direction="row" spacing={0.5} gap={0.5} alignItems="center" flexWrap="wrap" justifyContent={'flex-start'}>
                    {visibleItems.map((item, index) => (
                        <Paper
                            variant='outlined'
                            key={index}
                            sx={{ display: 'flex', boxShadow: 'none', alignItems: 'center', borderRadius: 5, backgroundColor: 'background.paper' }}
                        >
                            <Tooltip title={formatDateItem(item)} placement="top" arrow>
                                 <Chip clickable={false}
                            onClick={() => {}}
                                    label={formatDateItem(item)}
                                    color='inherit'
                                    size="small"
                                    sx={{
                                        backgroundColor: 'background.default',
                                        maxWidth: '210px', // Explicit max width
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap', // Ensure single-line text
                                    }}
                                />
                            </Tooltip>
                        </Paper>
                    ))}

                    {hiddenItems.length > 0 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 0.5 }}>

                            <Collapse in={showAll}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 0.5, justifyContent: 'flex-start',
                                    }}
                                >
                                    {hiddenItems.map((item, index) => (
                                        <Paper
                                            variant='outlined'
                                            key={index}
                                            sx={{
                                                display: 'flex', boxShadow: 'none',
                                                alignItems: 'center', borderRadius: 5, backgroundColor: 'background.paper',
                                            }}
                                        >
                                            <Tooltip title={formatDateItem(item)} placement="top" arrow>
                                                 <Chip clickable={false}
                            onClick={() => {}}
                                                    label={formatDateItem(item)}
                                                    color='inherit'
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: 'background.default',
                                                        maxWidth: '200px', // Explicit max width
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap', // Ensure single-line text
                                                    }}
                                                />
                                            </Tooltip>
                                        </Paper>

                                    ))}
                                </Paper>
                            </Collapse>

                            <Button
                                variant='text'
                                size='small'
                                onClick={handleClickShowAllDates}
                                sx={{ textTransform: 'none', color: 'text.secondary', width: 'max-content' }}
                                startIcon={showAll ? <ExpandLess /> : <ExpandMore />}
                            >
                                {showAll ? 'Hide Dates' : `Show ${hiddenItems.length} More Dates`}
                            </Button>


                        </Box>
                    )}
                </Stack>
            )}

        </>
    );
};

export default DateChips;


